var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createTheme } from '@mui/material';
import textDefinitions, { fontFamily } from '../textDefinitions';
import colors from './colors';
export var shadows = {
    "default": '0 0 16px rgba(0,0,0,0.1)',
    light3: '0px 3px 6px 3px rgba(82, 99, 99, 0.1)',
    light2: '0 2px 3px 1px rgba(126 ,126 ,126 ,0.15)',
    dark2: '0px 2px 4px 2px rgba(25, 33, 33, 0.25)',
    dark3: '0 3px 4px 1px rgba(126 ,126 ,126 ,0.25)'
};
var legacyTheme = createTheme({
    components: {
        MuiTypography: {
            styleOverrides: {
                root: {
                    transition: 'color .15s linear'
                }
            }
        },
        /* Button styles */
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    '&.MuiButtonBase-root': {
                        textTransform: 'none'
                    }
                }
            }
        },
        MuiButton: {
            variants: [
                {
                    props: { size: 'small' },
                    style: {
                        padding: '6px 8px',
                        borderRadius: 4
                    }
                },
                {
                    props: { size: 'medium' },
                    style: {
                        padding: '8px 12px',
                        borderRadius: 8
                    }
                },
                {
                    props: { size: 'large' },
                    style: {
                        padding: '12px 16px',
                        borderRadius: 8,
                        borderWidth: '1px !important',
                        height: 160,
                        width: 207
                    }
                },
                {
                    props: { size: 'large', variant: 'outlined', color: 'primary' },
                    style: {
                        borderColor: "".concat(colors.neutral20, " !important")
                    }
                },
                {
                    props: { variant: 'outlined', size: 'small' },
                    style: {
                        padding: '6px 14px'
                    }
                },
                {
                    props: { variant: 'outlined', size: 'medium' },
                    style: {
                        padding: '10px 14px'
                    }
                },
            ],
            styleOverrides: {
                root: {
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontFamily: fontFamily,
                    '&.Mui-disabled': {
                        cursor: 'not-allowed'
                    }
                },
                // Contained
                contained: {
                    '&.Mui-disabled': {
                        backgroundColor: colors.neutral5,
                        '& > *': {
                            color: colors.neutral30
                        }
                    }
                },
                containedPrimary: {
                    '&:hover': {
                        backgroundColor: colors.neutral90
                    },
                    '&:active': {
                        backgroundColor: colors.neutral80
                    }
                },
                containedSecondary: {
                    '&:hover': {
                        backgroundColor: colors.neutral10
                    },
                    '&:active': {
                        backgroundColor: colors.neutral20
                    }
                },
                containedSuccess: {
                    '&:hover': {
                        backgroundColor: colors.green70
                    },
                    '&:active': {
                        backgroundColor: colors.green80
                    },
                    '&:disabled': {
                        backgroundColor: colors.green10
                    }
                },
                containedDanger: {
                    '&:hover': {
                        backgroundColor: colors.red70
                    },
                    '&:active': {
                        backgroundColor: colors.red80
                    },
                    '&:disabled': {
                        backgroundColor: colors.red5
                    }
                },
                containedWarning: {
                    '&:hover': {
                        backgroundColor: colors.yellow40
                    },
                    '&:active': {
                        backgroundColor: colors.yellow50
                    },
                    '&:disabled': {
                        backgroundColor: colors.yellow5
                    }
                },
                // Outlined
                outlined: {
                    backgroundColor: colors.white,
                    borderWidth: 2,
                    borderStyle: 'solid',
                    '&.Mui-disabled': {
                        backgroundColor: colors.neutral0,
                        borderColor: colors.neutral30,
                        borderWidth: 2,
                        '& > *': {
                            color: colors.neutral30
                        }
                    },
                    '&:hover': {
                        borderWidth: 2
                    }
                },
                outlinedPrimary: {
                    borderColor: colors.neutral100,
                    '&:hover': {
                        backgroundColor: colors.neutral0
                    },
                    '&:active': {
                        backgroundColor: colors.neutral10
                    }
                },
                outlinedSecondary: {
                    borderColor: colors.neutral20,
                    '&:hover': {
                        backgroundColor: colors.neutral5,
                        borderColor: colors.neutral20
                    },
                    '&:active': {
                        backgroundColor: colors.neutral10,
                        borderColor: colors.neutral40
                    },
                    '&.Mui-disabled': {
                        backgroundColor: colors.neutral0,
                        borderColor: colors.neutral10
                    }
                },
                outlinedSuccess: {
                    borderColor: colors.green60,
                    '&:hover': {
                        backgroundColor: colors.green5
                    },
                    '&:active': {
                        backgroundColor: colors.green10
                    },
                    '&:disabled': {
                        backgroundColor: colors.green0,
                        borderColor: colors.green5,
                        '& > *': {
                            color: colors.green5
                        }
                    }
                },
                outlinedDanger: {
                    borderColor: colors.red60,
                    '&:hover': {
                        backgroundColor: colors.red5
                    },
                    '&:active': {
                        backgroundColor: colors.red10
                    },
                    '&:disabled': {
                        backgroundColor: colors.red0,
                        borderColor: colors.red5,
                        '& > *': {
                            color: colors.red5
                        }
                    }
                },
                outlinedWarning: {
                    borderColor: colors.yellow30,
                    '&:hover': {
                        backgroundColor: colors.yellow5
                    },
                    '&:active': {
                        backgroundColor: colors.yellow10
                    },
                    '&:disabled': {
                        backgroundColor: colors.yellow0,
                        borderColor: colors.yellow10,
                        '& > *': {
                            color: colors.yellow10
                        }
                    }
                },
                // Plain (or Text)
                text: {
                    backgroundColor: colors.white,
                    '&.Mui-disabled': {
                        '& > *': {
                            color: colors.neutral30
                        }
                    }
                },
                textPrimary: {
                    '&:hover': {
                        backgroundColor: colors.neutral5
                    },
                    '&:active': {
                        backgroundColor: colors.neutral10
                    }
                }
            }
        },
        /* End Button styles */
        /* Input styles */
        MuiFormControl: {
            variants: [
                {
                    props: { variant: 'standard' },
                    style: {
                        '& .MuiInputBase-root input': {
                            padding: '3px 8px'
                        },
                        '& .MuiInputBase-root:not(.Mui-focused).MuiInputBase-root:not(.Mui-focused) fieldset': {
                            borderColor: 'transparent'
                        }
                    }
                },
            ]
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    padding: 0
                }
            }
        },
        MuiOutlinedInput: {
            variants: [
                {
                    props: { error: false },
                    style: {
                        '&:not(.Mui-focused) .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.neutral60
                        },
                        '& .MuiInputBase-input.MuiInputBase-input': {
                            '&:focus + fieldset': {
                                borderColor: colors.neutral100
                            },
                            '&::placeholder': {
                                color: colors.neutral60
                            }
                        }
                    }
                },
                {
                    props: { error: true },
                    style: {
                        '& .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.red60
                        }
                    }
                },
                {
                    props: { disabled: true },
                    style: {
                        backgroundColor: colors.neutral0,
                        '& .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.neutral20
                        },
                        '& .MuiInputBase-input.MuiInputBase-input': {
                            'text-fill-color': colors.neutral20,
                            '&::placeholder': {
                                'text-fill-color': colors.neutral10
                            }
                        }
                    }
                },
                {
                    props: { size: 'small' },
                    style: {
                        '& .MuiOutlinedInput-input': __assign({ padding: '8px 16px' }, textDefinitions.body2Medium)
                    }
                },
                {
                    props: { size: 'medium' },
                    style: {
                        '& .MuiOutlinedInput-input': __assign({}, textDefinitions.body1),
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 8
                        }
                    }
                },
                /* special cases for inputs */
                {
                    props: {
                        // this prop does not exist in the MuiOutlinedInputProps interface, had to force it using @ts-ignore
                        block: true
                    },
                    style: {
                        '& .MuiOutlinedInput-input.MuiOutlinedInput-input': {
                            opacity: 0,
                            '&::placeholder': {
                                opacity: 0
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
                            zIndex: 1,
                            backgroundColor: colors.neutral0,
                            borderColor: colors.neutral20
                        }
                    }
                },
            ],
            styleOverrides: {
                notchedOutline: {
                    borderColor: colors.neutral60
                },
                root: {
                    borderRadius: 4
                },
                input: {
                    color: colors.neutral100,
                    fontFamily: fontFamily,
                    fontSize: '16px',
                    lineHeight: '24px',
                    fontWeight: '400',
                    padding: '12px 16px',
                    '&::placeholder': __assign(__assign({}, textDefinitions.body2Medium), { color: colors.neutral60, opacity: 1 })
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                positionStart: {
                    marginRight: 0
                },
                positionEnd: {
                    marginLeft: 0
                },
                root: {
                    '& svg, & path': {
                        fill: colors.neutral100
                    }
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    border: "2px solid ".concat(colors.neutral30),
                    color: colors.neutral60,
                    fontSize: '14px',
                    fontStyle: 'normal',
                    lineHeight: '16px',
                    fontWeight: 500
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: __assign(__assign({}, textDefinitions.body3), { fontWeight: 'medium', marginLeft: 0 })
            }
        },
        MuiFormLabel: {
            variants: [
                {
                    // @ts-ignore
                    props: { size: 'normal', 'data-shrink': false },
                    style: {
                        transform: 'translate(14px, 11px) scale(1) !important'
                    }
                },
                {
                    // @ts-ignore
                    props: { size: 'small', 'data-shrink': false },
                    style: {
                        transform: 'translate(14px, 6px) scale(1) !important'
                    }
                },
            ],
            styleOverrides: {
                root: {
                    color: colors.neutral80,
                    backgroundColor: colors.white,
                    padding: '0 4px'
                }
            }
        },
        /* End Input styles */
        /* Toggle Buttons styles */
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    '&.toggle-button-theme-legacy': {
                        gap: '8px',
                        display: 'flex',
                        // This is to accomodate the styles for the Insights page until the big refactor is done.
                        '&.new-toggle-button-theme': {
                            gap: '12px'
                        },
                        '&.toggle-bar-theme': {
                            gap: '0'
                        }
                    }
                },
                grouped: {
                    '&.toggle-button-theme-legacy': {
                        height: 140,
                        flex: 1,
                        '&:not(:first-of-type)': {
                            border: "1px solid ".concat(colors.neutral30),
                            borderRadius: '8px'
                        },
                        '&:not(:last-of-type)': {
                            border: "1px solid ".concat(colors.neutral30),
                            borderRadius: '8px'
                        },
                        '&.Mui-selected': {
                            '&:hover': {
                                backgroundColor: colors.neutral5
                            },
                            backgroundColor: colors.white,
                            borderWidth: 2,
                            borderColor: "".concat(colors.neutral100)
                        },
                        '&.new-toggle-button-theme': {
                            height: 'unset',
                            flex: 'unset'
                        }
                    }
                }
            }
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    '&.toggle-button-theme-legacy': {
                        '& .MuiTypography-root': __assign({}, textDefinitions.h6),
                        transition: 'background .05s linear',
                        '&:hover': {
                            backgroundColor: colors.neutral0
                        },
                        '&:active': {
                            backgroundColor: colors.neutral10
                        },
                        // This is to accomodate the styles for the Insights page until the big refactor is done.
                        '&.new-toggle-button-theme.new-toggle-button-theme': {
                            backgroundColor: 'transparent',
                            borderRadius: '4px',
                            border: "2px solid ".concat(colors.neutral70),
                            padding: 0,
                            margin: 0,
                            '& :is(svg, path)': {
                                fill: "".concat(colors.neutral70)
                            },
                            '&:active': {
                                backgroundColor: colors.neutral80
                            },
                            '&.Mui-selected': {
                                backgroundColor: colors.neutral80,
                                '&:hover': {
                                    backgroundColor: colors.neutral70
                                },
                                '& :is(svg, path)': {
                                    fill: "".concat(colors.proximaGreen20)
                                }
                            },
                            // This is to undo the h6 default applied to the original toggle buttons
                            '& .MuiTypography-root': {
                                fontSize: 'unset',
                                lineHeight: 'unset',
                                fontWeight: 'unset',
                                fontStyle: 'unset',
                                letterSpacing: 'unset'
                            }
                        },
                        '&.toggle-bar-theme': {
                            '&:not(:last-of-type)': {
                                borderRight: 'none',
                                borderTopRightRadius: '0',
                                borderBottomRightRadius: '0'
                            },
                            '&:not(:first-of-type)': {
                                borderTopLeftRadius: '0',
                                borderBottomLeftRadius: '0'
                            }
                        }
                    }
                }
            }
        },
        /* End Toggle Buttons styles */
        /* Checkbox styles */
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    marginLeft: 2
                }
            }
        },
        /* End Checkbox styles */
        /* Card styles */
        MuiCard: {
            styleOverrides: {
                root: {
                    padding: 32,
                    border: "2px solid ".concat(colors.neutral100),
                    boxShadow: shadows.dark2,
                    maxWidth: 'unset',
                    borderRadius: 16
                }
            }
        },
        /* End Card styles */
        /* Divider styles */
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: colors.neutral10
                }
            }
        },
        /* End Divider styles */
        /* Link styles */
        MuiLink: {
            styleOverrides: {
                root: {
                    textDecoration: 'none'
                }
            }
        },
        /* End Link styles */
        /* Table styles */
        MuiTableHead: {
            variants: [
                {
                    props: { datatype: 'altered' },
                    style: {
                        '& .MuiTableCell-root': {
                            backgroundColor: colors.neutral0,
                            borderRight: 'none !important',
                            borderBottom: "1px solid ".concat(colors.neutral10, " !important")
                        }
                    }
                },
            ],
            styleOverrides: {
                root: {
                    '& .MuiTableCell-root': {
                        borderBottom: "1px solid ".concat(colors.neutral5, " !important")
                    }
                }
            }
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    position: 'relative'
                }
            }
        },
        MuiTableRow: {
            variants: [
                {
                    props: { datatype: 'altered' },
                    style: {
                        '&.MuiTableRow-hover:is(:nth-of-type(even)):hover': {
                            backgroundColor: colors.white
                        },
                        '& .MuiTableCell-root': {
                            border: 'none !important'
                        }
                    }
                },
            ],
            styleOverrides: {
                root: {
                    backgroundColor: colors.white,
                    '&.MuiTableRow-hover:hover': {
                        '& .MuiTableCell-root': {
                            transition: 'background-color .05s linear',
                            backgroundColor: colors.neutral0
                        }
                    },
                    '& .MuiTableCell-root': {
                        borderBottom: "1px solid ".concat(colors.neutral5)
                    }
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    height: 0,
                    padding: '16px 16px 15px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    '&:not(:last-of-type)': {
                        borderRight: "1px solid ".concat(colors.neutral5)
                    }
                }
            }
        },
        /* End Table styles */
        /* Modal styles */
        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiBackdrop-root': {
                        backgroundColor: "rgba(126, 126, 126, 0.65)"
                    }
                },
                paper: {
                    borderRadius: 8,
                    border: "1px solid ".concat(colors.neutral20),
                    boxShadow: shadows.light3
                },
                paperFullScreen: {
                    borderRadius: 0,
                    border: 'none'
                }
            }
        },
        /* End Modal styles */
        /* Tooltip styles */
        MuiTooltip: {
            styleOverrides: {
                tooltip: __assign(__assign({}, textDefinitions.body2Semibold), { color: colors.cosmosCream, backgroundColor: colors.neutral100, border: "1px solid ".concat(colors.neutral80), borderRadius: '4px' }),
                arrow: {
                    '&:before': {
                        border: "1px solid ".concat(colors.neutral80)
                    },
                    color: colors.neutral100
                }
            }
        },
        /* End Tooltip styles */
        /* Menu styles */
        MuiMenu: {
            styleOverrides: {
                root: {
                    borderRadius: 16
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    backgroundColor: colors.white,
                    border: "2px solid ".concat(colors.legacy.mistDarker),
                    boxShadow: "".concat(shadows.light2, " !important")
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    padding: '12px 24px',
                    minWidth: 200,
                    // using class names for the Select dropdown padding so it doesn't affect other components using MenuItem
                    '&.select-menu-item-small': {
                        padding: '4px 8px'
                    },
                    '&.select-menu-item-medium': {
                        padding: '8px'
                    },
                    '&.select-menu-item-large': {
                        padding: '12px 16px'
                    }
                }
            }
        },
        /* Radio Group styles */
        MuiRadio: {
            styleOverrides: {
                root: {
                    '[data-testId=RadioButtonUncheckedIcon]': {
                        color: colors.neutral30
                    },
                    '[data-testId*=RadioButton]': {
                        fontSize: '1.2em',
                        strokeWidth: '1px'
                    }
                }
            }
        },
        /* End Radio Group styles */
        /* End Menu styles */
        /* Tabs Styles */
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    gap: '24px'
                },
                indicator: {
                    transitionDuration: '.15s',
                    height: '4px',
                    borderRadius: '1px',
                    backgroundColor: colors.neutral100
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        color: colors.neutral100
                    },
                    color: colors.neutral70,
                    padding: '16px 8px',
                    minWidth: 'unset'
                }
            }
        }
    }
});
export default legacyTheme;
